import { useCallback, useEffect, useState } from 'react';
import { attendanceAPI, departmentsAPI, usersAPI } from '../services/api';
import { CalendarDays, Check, Pencil, Plus, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptyLeaveForm = {
  user_id: '',
  start_date: '',
  end_date: '',
  reason: '',
  leave_type: 'paid',
  day_portion: 'full_day',
};

const Leaves = () => {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('leave.manage');
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyLeaveForm);

  const loadData = useCallback(async () => {
    const [leaveRes, usersRes, departmentsRes] = await Promise.all([
      attendanceAPI.getLeaves({
        department_id: departmentFilter || undefined,
        status: statusFilter || undefined,
      }),
      canManage ? usersAPI.getAll() : Promise.resolve({ data: { users: [] } }),
      canManage ? departmentsAPI.getAll() : Promise.resolve({ data: { departments: [] } }),
    ]);

    setLeaves(leaveRes.data.leaves || []);
    setUsers((usersRes.data.users || []).filter((user) => user.is_active));
    setDepartments(departmentsRes.data.departments || []);
  }, [canManage, departmentFilter, statusFilter]);

  useEffect(() => {
    loadData().catch(() => toast.error('Failed to load leave data'));
  }, [loadData]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyLeaveForm);
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if ((canManage && !form.user_id) || !form.start_date || !form.end_date || form.reason.trim().length < 3) {
      toast.error('User, date range, and reason are required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        user_id: canManage ? Number(form.user_id) : undefined,
      };

      if (editingId) {
        await attendanceAPI.updateLeave(editingId, payload);
        toast.success('Leave updated');
      } else {
        await attendanceAPI.createLeave(payload);
        toast.success('Leave created');
      }

      resetForm();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save leave');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (leave) => {
    setEditingId(leave.id);
    setForm({
      user_id: leave.user_id || '',
      start_date: String(leave.start_date || '').slice(0, 10),
      end_date: String(leave.end_date || '').slice(0, 10),
      reason: leave.reason || '',
      leave_type: leave.leave_type || 'paid',
      day_portion: leave.day_portion || 'full_day',
    });
    setShowForm(true);
  };

  const handleCancel = async (leave) => {
    try {
      await attendanceAPI.cancelLeave(leave.id);
      toast.success('Leave request cancelled');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel leave');
    }
  };

  const handleReview = async (leave, status) => {
    try {
      await attendanceAPI.reviewLeave(leave.id, { status });
      toast.success(`Leave ${status}`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to review leave');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leaves</h1>
          <p className="mt-1 text-sm text-gray-500">Approve leave so absences are counted correctly.</p>
        </div>
        <button
            onClick={() => setShowForm((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : 'Request Leave'}
          </button>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-4">
        {canManage && <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All departments</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>{department.name}</option>
            ))}
          </select>
        </div>}

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
          {canManage && <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">User</label>
            <select
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name} {user.employee_id ? `(${user.employee_id})` : ''}
                </option>
              ))}
            </select>
          </div>}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Leave Type</label>
            <select
              value={form.leave_type}
              onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="paid">Paid</option><option value="sick">Sick</option>
              <option value="casual">Casual</option><option value="unpaid">Unpaid</option><option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Day Portion</label>
            <select value={form.day_portion} onChange={(e) => setForm({ ...form, day_portion: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="full_day">Full day</option><option value="first_half">First half</option><option value="second_half">Second half</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Reason</label>
            <textarea
              rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Medical leave, seminar, approved duty leave..."
            />
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? 'Update Request' : 'Submit Request'}
            </button>
            <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {leaves.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No leave requests found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Dates</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Reason</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {leaves.map((leave) => (
                <tr key={leave.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <CalendarDays size={16} className="text-blue-600" />
                      <div>
                        <div>{leave.full_name}</div>
                        <div className="text-xs text-gray-500">{leave.employee_id || leave.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{leave.department_name || 'No department'}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                      leave.status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : leave.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      {leave.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{leave.leave_type} · {String(leave.day_portion).replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-gray-600">{leave.reason || '-'}</td>
                  <td className="px-4 py-3">
                    {leave.status === 'pending' ? (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(leave)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        {canManage && Number(leave.user_id) !== Number(user?.id) && <>
                          <button onClick={() => handleReview(leave, 'approved')} className="inline-flex items-center gap-1 rounded-lg border border-green-200 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50"><Check size={12}/>Approve</button>
                          <button onClick={() => handleReview(leave, 'rejected')} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"><X size={12}/>Reject</button>
                        </>}
                        {(canManage || Number(leave.user_id) === Number(user?.id)) && <button onClick={() => handleCancel(leave)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"><X size={12}/>Cancel</button>}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Leaves;
